import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

import {POST_RCS_BOTS_ADD_TESTER} from "../../config/urls";

// ── Design System Imports (Adjust paths as needed) ──
import typography from "../../cssUtils/typography";
import { ColorsConstants, borderColors, inputColor } from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import { CallingCodeSelect } from "../../common/country-code-select";
import { useTenantCountryDefault } from "../../hooks/useTenantCountry";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    ...typography.bodyLarge,
    color: inputColor,
    backgroundColor: ColorsConstants.primaryWhite,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
};

export default function AddTesterModal({ open, onClose, botId, onSuccess }) {
  const [countryCode, setCountryCode] = useState("");
  // Starts on the tenant's country (Tenant Settings)
  useTenantCountryDefault(countryCode, setCountryCode, { format: "+code" });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setCountryCode("");
    setPhoneNumber("");
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!phoneNumber.trim()) {
      setError("Please enter a phone number.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const tenant = localStorage.getItem("tenant");
      
      // Call your new API endpoint
      const response = await axios.post(
        // Replace with your actual base URL if needed
        POST_RCS_BOTS_ADD_TESTER, 
        {
          bot: botId,
          country_code: countryCode,
          phone_number: phoneNumber
        },
        {
          headers: { "X-Tenant-ID": tenant }
        }
      );

      if (response.status === 201) {
        onSuccess(response.data);
        handleClose();
      }
    } catch (err) {
      console.error("Failed to add tester:", err);
      setError(
        err.response?.data?.details || 
        err.response?.data?.error || 
        "An error occurred while adding the tester."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      PaperProps={{
        sx: { width: "100%", maxWidth: 480, borderRadius: "12px" }
      }}
    >
      <DialogTitle sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        pb: 1,
        borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`
      }}>
        <Typography sx={{ ...typography.h2, color: ColorsConstants.primaryBlue100 }}>
          Add Tester Device
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography sx={{ ...typography.bodyMedium, color: ColorsConstants.grayFontColor, mb: 3 }}>
          Enter the phone number you want to whitelist. The user must tap "Accept" on the system message they receive before you can send them test messages.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "8px", ...typography.bodySmall }}>
            {typeof error === 'object' ? JSON.stringify(error) : error}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* Country Code Input */}
          <CallingCodeSelect
            value={countryCode}
            onChange={(code) => setCountryCode(`+${code}`)}
            label=""
            sx={{ width: 220, flexShrink: 0 }}
            TextFieldProps={{ sx: FIELD_SX }}
          />
          
          {/* Phone Number Input */}
          <TextField
            fullWidth
            value={phoneNumber}
            onChange={(e) => {
              // Only allow numbers
              const val = e.target.value.replace(/\D/g, "");
              setPhoneNumber(val);
            }}
            placeholder="98765 43210"
            sx={FIELD_SX}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: ColorsConstants.inputFieldGrey }}>📱</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, mt: 1 }}>
        <ButtonOne 
          variant="outlined" 
          onClick={handleClose} 
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Cancel
        </ButtonOne>
        <ButtonOne onClick={handleSubmit} disabled={loading || !phoneNumber}>
          {loading ? (
            <CircularProgress size={20} sx={{ color: ColorsConstants.primaryWhite }} />
          ) : (
            "Send Invite"
          )}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
}